import { useEffect, useRef, useState } from 'react';
import { Copy, Check } from 'lucide-react';
import { toast } from 'react-toastify';
import { copyToClipboard } from '../../utils/clipboard';
import './CopyButton.scss';

interface CopyButtonProps {
  /** The text placed on the clipboard. Empty/whitespace disables the button. */
  value: string | null | undefined;
  /** What is being copied, for the tooltip and aria-label — e.g. "Ship to address". */
  label: string;
  size?: number;
  className?: string;
  disabled?: boolean;
}

/**
 * A small icon button that copies `value` to the clipboard and confirms in place
 * — the icon swaps to a green tick for ~1.5s rather than firing a toast on
 * success (a failure still toasts, since the copy silently didn't land).
 *
 * It only ever reads, so dropping one next to a form field can't dirty the form.
 */
export function CopyButton({ value, label, size = 15, className, disabled }: CopyButtonProps) {
  const [copied, setCopied] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(
    () => () => {
      if (timer.current) clearTimeout(timer.current);
    },
    []
  );

  const text = (value ?? '').trim();
  const isDisabled = disabled || text === '';

  const handleCopy = async () => {
    if (isDisabled) return;

    if (!(await copyToClipboard(text))) {
      toast.error(`Could not copy the ${label.toLowerCase()}`);
      return;
    }

    setCopied(true);
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => setCopied(false), 1500);
  };

  const action = copied ? `${label} copied` : `Copy ${label.toLowerCase()}`;

  return (
    <button
      type="button"
      className={`copy-button${copied ? ' copy-button--copied' : ''}${
        className ? ` ${className}` : ''
      }`}
      onClick={handleCopy}
      disabled={isDisabled}
      title={copied ? 'Copied' : action}
      aria-label={action}
    >
      {copied ? <Check size={size} /> : <Copy size={size} />}
    </button>
  );
}

export default CopyButton;
